<template>
  <div>
    <!-- Repeat Task Dialog -->
    <v-dialog v-model="mainDialogState" transition="dialog-bottom-transition" fullscreen>
      <v-card class="repeat-task-dialog">
        <v-toolbar class="bg-primary">
          <v-toolbar-title class="heading">Repeat Task</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="mainDialogState = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <!-- Top Sub Nav -->
        <div id="view-sub-nav">
          <v-btn variant="outlined" color="primary" @click="openRepeatSubDialog()">
            New Repeat Task
            <v-icon end>mdi-plus</v-icon>
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn variant="flat" color="success" @click="createScheduledTasksFromRepeat(repeatTaskData)">
            Schedule Repeat Tasks
            <v-icon end>mdi-calendar-plus</v-icon>
          </v-btn>
        </div>

        <!-- Data Table for Repeat Tasks -->
        <v-data-table
          v-if="repeatTaskDataForDisplay.length > 0"
          :items="repeatTaskDataForDisplay"
          :headers="repeatTaskTableHeaders"
          :sort-by="[{ key: 'name', order: 'asc' }]"
          :items-per-page="-1"
          id="repeat-task-table"
          :loading="repeatTaskDataLoading"
        >
          <!-- Task Type as a Chip -->
          <template v-slot:item.type="{ item }">
            <v-chip
              class="coloured"
              :class="item.type == 'dispatch' ? `chip--${item.subtype}` : `chip--${item.type}`"
              size="small"
              >{{ item.type == 'dispatch' ? item.type + ' ' + item.subtype : item.type }}</v-chip
            >
          </template>

          <template v-slot:item.mu="{ item }">
            {{ $utils.toFixedNumber(item.mu, 1) }}
          </template>

          <!-- Dates as Multiple Chips -->
          <template v-slot:item.dates="{ item }">
            <div class="d-flex flex-wrap">
              <template v-if="pillDates(item).length">
                <v-chip
                  v-for="(date, index) in pillDates(item)"
                  :key="index"
                  class="ma-1"
                  color="primary"
                  size="small"
                  style="padding: 4px 12px; font-size: 0.875rem"
                >
                  {{ $moment(date.toDate ? date.toDate() : date).format('MMM D, ddd') }}
                </v-chip>
              </template>
              <span v-else class="text-grey-darken-1">Set Repeat Dates</span>
            </div>
          </template>

          <!-- Actions Column -->
          <template v-slot:item.actions="{ item }">
            <td class="d-flex justify-end align-center">
              <v-tooltip location="top" :open-delay="500">
                <template v-slot:activator="{ props }">
                  <v-icon size="small" class="mr-1" @click="openRepeatSubDialog(item)" v-bind="props"
                    >mdi-pencil</v-icon
                  >
                </template>
                <span>Update Repeat Task</span>
              </v-tooltip>
              <v-tooltip location="top" :open-delay="500">
                <template v-slot:activator="{ props }">
                  <v-icon
                    size="small"
                    @click="
                      () => {
                        ;(deleteRepeatTaskDialog = true), (selectedItem = item)
                      }
                    "
                    v-bind="props"
                    >mdi-delete</v-icon
                  >
                </template>
                <span>Delete Repeat Task</span>
              </v-tooltip>
            </td>
          </template>
        </v-data-table>
      </v-card>
    </v-dialog>

    <!-- Create/Update Repeat Sub Dialog -->
    <v-dialog v-model="repeatSubDialog" width="800">
      <v-card>
        <v-card-title class="bg-primary text-white text-h6">
          {{ dialogMode == 'Create' ? 'Create Repeat Task' : 'Update Repeat Task' }}
        </v-card-title>
        <v-card-text>
          <v-form class="py-5 mx-4" ref="form" validate-on="blur">
            <div>
              <v-row class="mb-5">
                <v-select
                  v-model="selectedItem.type"
                  :items="taskTypes"
                  label="Task Type"
                  hide-details
                  :rules="[$rules.required('task type')]"
                  required
                ></v-select>
              </v-row>
              <v-row class="mb-5">
                <v-select
                  v-if="selectedItem.type && taskSubTypes[selectedItem.type]"
                  v-model="selectedItem.subtype"
                  :items="taskSubTypes[selectedItem.type]"
                  label="Task Sub-Type"
                  hide-details
                  :rules="[$rules.required('task subtype')]"
                  required
                ></v-select>
              </v-row>
              <v-row>
                <v-text-field
                  label="Task Name"
                  v-model="selectedItem.name"
                  :rules="[$rules.required('task name')]"
                  required
                ></v-text-field>
              </v-row>
              <v-row>
                <v-text-field
                  type="number"
                  label="Units"
                  v-model="selectedItem.units"
                  :rules="[$rules.required('task units')]"
                  required
                ></v-text-field>
              </v-row>
              <v-row>
                <v-text-field
                  type="number"
                  label="Work Minutes"
                  v-model="selectedItem.workMinutes"
                  :rules="[$rules.required('task work minutes')]"
                  required
                ></v-text-field>
              </v-row>
              <v-row>
                <v-text-field
                  type="number"
                  label="Mega Units"
                  v-model="selectedItem.mu"
                  disabled
                  :rules="[$rules.required('task mu')]"
                  required
                ></v-text-field>
              </v-row>
            </div>
            <v-row class="my-4">
              <v-menu :close-on-content-click="false" transition="scale-transition" location="end" min-width="auto">
                <template v-slot:activator="{ props }">
                  <v-btn v-bind="props" color="primary" class="mb-2">
                    <v-icon start>mdi-calendar</v-icon>
                    Select Repeat Dates
                  </v-btn>
                </template>
                <v-card>
                  <MinimalDatePicker
                    v-model="selectedItem.dates"
                    multiple
                    :min="today"
                    :max="$moment(today).add(2, 'month').format('YYYY-MM-DD')"
                  ></MinimalDatePicker>
                </v-card>
              </v-menu>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            variant="text"
            @click="
              () => {
                resetRepeatSubDialog(),
                  this.$store.commit('openSnackbar', {
                    snackbarText: 'Cancelled, Not Saved'
                  })
              }
            "
            >Cancel</v-btn
          >
          <v-btn color="accent" variant="flat" @click="validateRepeatTaskForm" :disabled="submitting">{{
            dialogMode === 'Create' ? 'Create' : 'Update'
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Repeat Task Confirmation Dialog -->
    <v-dialog v-model="deleteRepeatTaskDialog" width="500">
      <v-card>
        <v-card-title class="bg-primary text-white text-h6">Delete Repeat Task</v-card-title>
        <v-card-text class="mt-5">
          <v-row>
            Are you sure you want to delete the repeat task
            <strong>{{ selectedItem.name }}</strong
            >?
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="deleteRepeatTaskDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" @click="deleteRepeatTask">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { db } from '@/js/db'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  updateDoc,
  where,
  writeBatch
} from 'firebase/firestore'
import MinimalDatePicker from '@/components/ui/MinimalDatePicker'

export default {
  name: 'RepeatTaskDialog',
  components: { MinimalDatePicker },
  emits: ['update:modelValue'],
  props: {
    modelValue: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    mainDialogState: {
      get() {
        return this.modelValue
      },
      set(val) {
        this.$emit('update:modelValue', val)
      }
    },
    repeatTaskDataForDisplay() {
      return Array.isArray(this.repeatTaskData) ? this.repeatTaskData : []
    },
    effectiveDatesByRepeatId() {
      const byRepeat = new Map()
      for (const task of this.repeatGeneratedTasks) {
        if (!task.repeatTaskId) continue
        if (!byRepeat.has(task.repeatTaskId)) byRepeat.set(task.repeatTaskId, [])
        byRepeat.get(task.repeatTaskId).push(task)
      }

      const effective = {}
      for (const repeatTask of this.repeatTaskData) {
        const tasks = byRepeat.get(repeatTask.id)
        if (!tasks || !tasks.length) continue // no evidence: the pills fall back to `dates`
        try {
          const plan = this.planRepeatTaskReconcile(repeatTask, tasks, this.today)
          if (plan.hasEvidence) effective[repeatTask.id] = plan.dates
        } catch (err) {
          console.error(`Failed to derive dates for repeat task ${repeatTask.id}:`, err)
        }
      }
      return effective
    }
  },
  data() {
    return {
      taskTypes: [
        { value: 'cut', text: 'Cut' },
        { value: 'finish', text: 'Finish' },
        { value: 'assemble', text: 'Assemble' },
        { value: 'pack', text: 'Pack' },
        { value: 'dispatch', text: 'Dispatch' }
      ],
      taskSubTypes: {
        dispatch: [
          { value: 'supplier', text: 'To Badlwins' },
          { value: 'sprayer', text: 'To Sprayer' },
          { value: 'customer', text: 'To Customer' }
        ]
      },
      today: this.$moment().format('YYYY-MM-DD'),
      submitting: false,
      repeatTaskDialog: false,
      repeatTaskTableHeaders: [
        { title: 'Task Name', key: 'name' },
        { title: 'Task Type', key: 'type' },
        { title: 'Units', key: 'units' },
        { title: 'Work Minutes', key: 'workMinutes' },
        { title: 'Mega Units', key: 'mu' },
        { title: 'Date', key: 'dates' },
        { title: 'Actions', key: 'actions', align: 'end', sortable: false }
      ],
      repeatTaskData: [],
      repeatGeneratedTasks: [],
      repeatTaskDataLoading: true,
      selectedItem: {},
      dialogMode: null,
      repeatSubDialog: false,
      newItemForm: {
        name: '',
        type: '',
        subtype: '',
        units: 0,
        workMinutes: 0,
        mu: 0,
        dates: []
      },
      deleteRepeatTaskDialog: false
    }
  },
  created() {
    this._repeatUnsubs = []
  },
  beforeUnmount() {
    this.stopLiveRepeatData()
  },
  watch: {
    mainDialogState(val) {
      if (val) {
        this.onDialogOpen()
      } else {
        this.stopLiveRepeatData()
      }
    },
    'selectedItem.workMinutes': function (val) {
      if (val) {
        this.selectedItem.mu = this.$utils.toFixedNumber((val * 60) / 120, 1)
      } else {
        this.selectedItem.mu = null
      }
    }
  },
  methods: {
    // Repeat Task Dialog Methods
    async onDialogOpen() {
      // replaces mounted
      this.loadRepeatTaskData()
    },
    async openRepeatSubDialog(item) {
      this.resetRepeatSubDialog()

      if (item) {
        this.dialogMode = 'Update'
        const dates = this.pillDates(item)
          .map((raw) => this.repeatDateToJsDate(raw))
          .filter((d) => d && !isNaN(d.getTime()))

        this.selectedItem = { ...item, dates }
        this.newItemForm = { ...item, dates }
      } else {
        this.dialogMode = 'Create'
        this.selectedItem = this.newItemForm
      }
      this.repeatSubDialog = true
    },
    startLiveRepeatData() {
      this.stopLiveRepeatData()
      this.repeatTaskDataLoading = true

      this._repeatUnsubs.push(
        onSnapshot(
          collection(db, 'repeatTask'),
          (snapshot) => {
            this.repeatTaskData = snapshot.docs.map((snapDoc) => ({ id: snapDoc.id, ...snapDoc.data() }))
            this.repeatTaskDataLoading = false
          },
          (error) => {
            console.error('repeatTask listener failed:', error)
            this.repeatTaskDataLoading = false
          }
        )
      )

      this._repeatUnsubs.push(
        onSnapshot(
          query(collection(db, 'tasks'), where('repeatTaskId', '!=', null)),
          (snapshot) => {
            this.repeatGeneratedTasks = snapshot.docs.map((snapDoc) => ({ id: snapDoc.id, ...snapDoc.data() }))
          },
          (error) => {
            console.error('repeat task tasks listener failed:', error)
          }
        )
      )
    },
    stopLiveRepeatData() {
      this._repeatUnsubs.forEach((unsub) => unsub())
      this._repeatUnsubs = []
    },
    async loadRepeatTaskData() {
      this.resetRepeatSubDialog() // just to be safe

      this.startLiveRepeatData()
    },
    resetRepeatSubDialog() {
      this.newItemForm = {
        name: '',
        type: '',
        subtype: '',
        units: 0,
        workMinutes: 0,
        mu: 0,
        dates: []
      }
      this.selectedItem = {}
      this.repeatSubDialog = false
    },
    async validateRepeatTaskForm() {
      if (!(await this.$utils.validateForm(this.$refs.form))) return
      if (this.submitting) return
      this.submitting = true

      if (this.dialogMode === 'Create') {
        this.createRepeatTask()
      } else if (this.dialogMode === 'Update') {
        this.updateRepeatTask()
      }
    },
    async createRepeatTask() {
      if (!(await this.$utils.validateForm(this.$refs.form))) {
        this.$store.commit('openSnackbar', {
          snackbarText: 'Please fill in all required fields.'
        })
        return
      }

      let newRepeatTask = { ...this.selectedItem }
      newRepeatTask.dateCreated = this.$utils.tsNow()
      newRepeatTask.createdBy = this.$store.state.user.email

      // assign mu to the correct task type
      if (newRepeatTask.workMinutes && newRepeatTask.mu && newRepeatTask.type) {
        switch (newRepeatTask.type) {
          case 'cut':
            newRepeatTask.muCut = newRepeatTask.mu
            break
          case 'finish':
          case 'assemble':
            newRepeatTask.muFinish = newRepeatTask.mu
            break
          case 'pack':
            newRepeatTask.muPack = newRepeatTask.mu
            break
          case 'dispatch':
            newRepeatTask.muDispatch = newRepeatTask.mu
            break
          default:
            newRepeatTask.muUnits = newRepeatTask.mu
            break
        }
      }

      this.submitting = true

      try {
        await addDoc(collection(db, 'repeatTask'), newRepeatTask)
        this.resetRepeatSubDialog()
        this.$store.commit('openSnackbar', {
          snackbarText: 'Repeat Task Created'
        })

        // rebind
        await this.loadRepeatTaskData()
      } catch (error) {
        console.error('Error creating repeat task:', error)
      } finally {
        this.submitting = false
      }
    },
    async updateRepeatTask() {
      if (!(await this.$utils.validateForm(this.$refs.form))) {
        this.$store.commit('openSnackbar', {
          snackbarText: 'Please fill in all required fields.'
        })
        return
      }

      let updateRepeatTask = { ...this.selectedItem }
      updateRepeatTask.dateCreated = this.$utils.tsNow()
      updateRepeatTask.createdBy = this.$store.state.user.email

      // assign mu to the correct task type
      if (updateRepeatTask.workMinutes && updateRepeatTask.mu && updateRepeatTask.type) {
        switch (updateRepeatTask.type) {
          case 'cut':
            updateRepeatTask.muCut = updateRepeatTask.mu
            break
          case 'finish':
          case 'assemble':
            updateRepeatTask.muFinish = updateRepeatTask.mu
            break
          case 'pack':
            updateRepeatTask.muPack = updateRepeatTask.mu
            break
          case 'dispatch':
            updateRepeatTask.muDispatch = updateRepeatTask.mu
            break
          default:
            updateRepeatTask.muUnits = updateRepeatTask.mu
            break
        }
      }

      this.submitting = true
      try {
        await updateDoc(doc(db, 'repeatTask', this.selectedItem.id), updateRepeatTask)
        this.resetRepeatSubDialog()
        this.$store.commit('openSnackbar', {
          snackbarText: 'Repeat Task Updated'
        })

        // rebind
        await this.loadRepeatTaskData()
      } catch (error) {
        console.error('Error updating repeat task:', error)
      } finally {
        this.submitting = false
      }
    },
    async deleteRepeatTask() {
      this.submitting = true
      try {
        // Check if there are any scheduled tasks linked to this repeat task
        const scheduledTasksSnap = await getDocs(
          query(collection(db, 'tasks'), where('repeatTaskId', '==', this.selectedItem.id))
        )

        if (!scheduledTasksSnap.empty) {
          // delete all scheduled tasks linked to this repeat task
          const batch = writeBatch(db)
          scheduledTasksSnap.forEach((snapDoc) => {
            batch.delete(doc(db, 'tasks', snapDoc.id))
          })
          await batch.commit()
          this.$store.commit('openSnackbar', {
            snackbarText: 'All scheduled tasks linked to this repeat task have been deleted.'
          })
        } else {
          this.$store.commit('openSnackbar', {
            snackbarText: 'No scheduled tasks found for this repeat task.'
          })
        }

        // delete the repeat task itself
        await deleteDoc(doc(db, 'repeatTask', this.selectedItem.id))
        this.deleteRepeatTaskDialog = false
        this.$store.commit('openSnackbar', {
          snackbarText: 'Repeat Task Deleted'
        })

        // rebind
        await this.loadRepeatTaskData()
      } catch (error) {
        console.error('Error checking scheduled tasks:', error)
      } finally {
        this.submitting = false
      }
    },
    async createScheduledTasksFromRepeat(repeatTasks) {
      if (!repeatTasks || !Array.isArray(repeatTasks) || !repeatTasks.length) return

      const batch = writeBatch(db)

      for (const task of repeatTasks) {
        let plan
        try {
          plan = this.planRepeatTaskReconcile(task, await this.loadTasksForRepeat(task.id), this.today)
        } catch (err) {
          console.error(`Failed to reconcile repeat task ${task.id}:`, err)
          continue
        }

        if (plan.hasEvidence && plan.datesChanged) {
          const dates = plan.dates.map((d) => this.$utils.tsFromDate(d))
          batch.update(doc(db, 'repeatTask', task.id), { dates })
          task.dates = dates
        }

        for (const { id, repeatDate } of plan.stamp) {
          batch.update(doc(db, 'tasks', id), { repeatDate: this.$utils.tsFromDate(repeatDate) })
        }

        for (const id of plan.remove) {
          batch.delete(doc(db, 'tasks', id))
        }

        // Create missing tasks
        for (const createDate of plan.create) {
          const scheduledDate = this.$utils.tsFromDate(createDate)

          // Create task doc
          const taskDoc = {
            orderNumber: 1000,
            objectType: task.name,
            type: task.type,
            subtype: task.subtype,
            units: task.units,
            dateScheduled: scheduledDate,
            dateCreated: this.$utils.tsNow(),
            shipmentID: null,
            shipmentRef: null,
            targetDeliveryDate: null,
            manuallyCreated: true,
            complete: null,
            inProduction: true,
            repeatTaskId: task.id, // Link to the repeat task
            repeatDate: scheduledDate,
            createdBy: this.$store.state.user.email
          }

          // if no subtype then delete subtype
          if (!taskDoc.subtype || taskDoc.subtype === '') {
            delete taskDoc.subtype
          }

          const taskMu = this.$utils.toFixedNumber(task.mu, 1)
          switch (task.type) {
            case 'cut':
              taskDoc.muCut = taskMu
              break
            case 'finish':
            case 'assemble':
              taskDoc.muFinish = taskMu
              break
            case 'pack':
              taskDoc.muPack = taskMu
              break
            case 'dispatch':
              taskDoc.muDispatch = taskMu
              break
            default:
              taskDoc.muUnits = taskMu
          }

          batch.set(doc(collection(db, 'tasks')), taskDoc)
        }
      }
      try {
        await batch.commit()
        this.$store.commit('openSnackbar', {
          snackbarText: 'Scheduled Repeat Tasks Created'
        })
      } catch (err) {
        console.error('Failed to process scheduled tasks:', err)
      }
    },
    repeatDateToJsDate(date) {
      if (!date) return null
      if (typeof date.toDate === 'function') return date.toDate() // Firestore Timestamp
      if (date instanceof Date) return date // V3 picker
      if (typeof date === 'object' && typeof date.seconds === 'number') {
        return new Date(date.seconds * 1000) // plain {seconds, nanoseconds} map
      }
      return this.$moment(date, 'YYYY-MM-DD').toDate() // V2 'YYYY-MM-DD' string
    },
    pillDates(item) {
      const effective = this.effectiveDatesByRepeatId[item.id]
      if (Array.isArray(effective)) return effective

      if (!Array.isArray(item.dates)) return []
      return item.dates.filter((raw) => {
        const key = this.repeatDayKey(raw)
        return key && key >= this.today
      })
    },
    repeatDayKey(value) {
      const d = this.repeatDateToJsDate(value)
      if (!d || isNaN(d.getTime())) return null
      return this.$moment(d).format('YYYY-MM-DD')
    },
    async loadTasksForRepeat(repeatTaskId) {
      const snap = await getDocs(query(collection(db, 'tasks'), where('repeatTaskId', '==', repeatTaskId)))
      return snap.docs.map((snapDoc) => ({ id: snapDoc.id, ...snapDoc.data() }))
    },
    planRepeatTaskReconcile(repeatTask, existingTasks, todayKey) {
      const configured = new Map()
      for (const raw of Array.isArray(repeatTask.dates) ? repeatTask.dates : []) {
        const key = this.repeatDayKey(raw)
        if (key) configured.set(key, this.repeatDateToJsDate(raw))
      }

      const effective = new Map()
      const stamp = []
      const remove = []
      const claimed = new Set()
      let unaccountedLegacy = 0
      let tasksConsidered = 0

      for (const task of existingTasks) {
        const actualKey = this.repeatDayKey(task.dateScheduled)
        if (!actualKey) continue // unscheduled: nothing to reconcile a date against
        tasksConsidered++

        const anchorKey = task.repeatDate ? this.repeatDayKey(task.repeatDate) : null

        const legacy = anchorKey === null

        const anchorPruned = !legacy && anchorKey < todayKey
        const wanted = legacy || anchorPruned || configured.has(anchorKey)

        if (!wanted) {
          if (actualKey >= todayKey && !task.complete) remove.push(task.id)
          continue
        }

        if (anchorKey) claimed.add(anchorKey)
        if (legacy && !configured.has(actualKey) && actualKey >= todayKey) unaccountedLegacy++
        effective.set(actualKey, this.repeatDateToJsDate(task.dateScheduled))

        if (legacy || anchorKey !== actualKey) {
          stamp.push({ id: task.id, repeatDate: this.repeatDateToJsDate(task.dateScheduled) })
        }
      }

      let toAccountFor = unaccountedLegacy
      for (const key of [...configured.keys()].sort()) {
        if (toAccountFor === 0) break
        if (claimed.has(key) || effective.has(key)) continue
        claimed.add(key)
        toAccountFor--
      }

      const create = []
      for (const [key, date] of configured) {
        if (claimed.has(key) || effective.has(key) || key < todayKey) continue
        create.push(date)
        effective.set(key, date)
      }

      const keptKeys = [...effective.keys()].filter((key) => key >= todayKey).sort()
      const dates = keptKeys.map((key) => effective.get(key))

      const configuredKeys = [...configured.keys()].sort()
      const datesChanged = configuredKeys.join(',') !== keptKeys.join(',')

      const hasEvidence = tasksConsidered > 0

      return { id: repeatTask.id, dates, datesChanged, hasEvidence, stamp, create, remove }
    }
  }
}
</script>
